/* ==========================================================================
   jays-pdp-gallery.js — desktop photo gallery helper for the product page.
   Loaded by sections/main-product.liquid when the gallery layout is
   "thumbnail" (one big photo + a thumbnail rail). Added 2026-10-03 as part of
   the desktop buy-box fix. Two jobs, both desktop-only, both additive:

   1. LOOP THE ARROWS. The theme's slider stops at each end and greys out the
      arrow. Jordan wants the arrows to wrap: "next" on the last photo jumps to
      the first, "previous" on the first jumps to the last. We only do this on
      sliders that carry data-loop (set in snippets/product-media-gallery.liquid),
      so every other slider on the site keeps its stock behaviour.

   2. FIT THE PHOTO STAGE TO THE WINDOW. The stage pins under the header while
      the buy box scrolls. If the stage were taller than the window its bottom
      would be cut off, and if we just capped its height the photo would sit in
      a box wider than itself (letterboxing, which looks cheap). So we cap the
      WIDTH instead: stage width = the smaller of the column width and
      (room in the viewport × the photo's own aspect ratio). The box always
      matches the photo, edge to edge. The thumbnail rail is capped to the
      same height so the two line up.

   No dependencies beyond theme.js (which defines <slider-element>). Safe to
   delete: the page falls back to the theme's stock thumbnail gallery.
   ========================================================================== */
(() => {
  'use strict';

  /* ---------- 1. Looping arrows ---------- */
  const Slider = customElements.get('slider-element');
  if (Slider) {
    const proto = Slider.prototype;
    const stockNext = proto.next;
    const stockPrevious = proto.previous;
    const stockUpdateButtons = proto.updateButtons;
    const loops = (el) => el.hasAttribute('data-loop');

    proto.next = function () {
      if (loops(this) && this.currentPage >= this.itemsToShow.length) return this.select(1);
      return stockNext.call(this);
    };

    proto.previous = function () {
      if (loops(this) && this.currentPage <= 1) return this.select(this.itemsToShow.length);
      return stockPrevious.call(this);
    };

    // The stock version disables the arrow at each end. A looping slider never
    // has an end, so both arrows stay live whenever there is more than one photo.
    proto.updateButtons = function () {
      if (!loops(this)) return stockUpdateButtons.call(this);
      const status = this.itemsToShow.length > 1 ? 'visible' : 'hidden';
      this.dispatchEvent(new CustomEvent('slider:previousStatus', { bubbles: true, detail: { status } }));
      this.dispatchEvent(new CustomEvent('slider:nextStatus', { bubbles: true, detail: { status } }));
    };

    // A slider that initialised before this file ran has already registered the
    // STOCK next/previous functions as its event listeners (theme.js passes the
    // prototype methods straight to addEventListener). Swap them for the looping
    // versions and re-send the arrow status with the looping rules.
    document.querySelectorAll('slider-element[data-loop]').forEach((el) => {
      el.removeEventListener('slider:previous', stockPrevious);
      el.removeEventListener('slider:next', stockNext);
      el.addEventListener('slider:previous', el.previous);
      el.addEventListener('slider:next', el.next);
      if (typeof el.currentPage === 'number') el.updateButtons();
    });
  }

  /* ---------- 2. Fit the stage to the viewport ---------- */
  const desktop = window.matchMedia('(min-width: 1024px)');
  const GAP_ABOVE_AND_BELOW = 48; // 24px breathing room top + bottom, matches the theme's sticky offset

  const px = (value) => parseFloat(value) || 0;

  // The theme publishes the sticky header's height as --header-height, but it
  // measures inside requestAnimationFrame, which some loads (background tabs,
  // hot reloads) skip. Measure the header element directly and, when the
  // theme's variable is missing or zero, publish it ourselves so the theme's
  // own sticky offsets (which read the variable) line up too.
  const measureHeader = () => {
    const header = document.querySelector('.header-section') || document.querySelector('header.header');
    const measured = header ? header.getBoundingClientRect().height : 0;
    const published = px(getComputedStyle(document.documentElement).getPropertyValue('--header-height'));
    if (measured && !published) document.documentElement.style.setProperty('--header-height', measured.toFixed(1) + 'px');
    return Math.max(measured, published);
  };

  const fitGallery = (gallery) => {
    const container = gallery.querySelector('.product__media-container');
    const stage = container && container.querySelector(':scope > .relative');
    if (!stage) return;
    const rail = gallery.querySelector('.product__thumbnails--beside');
    const railList = rail && rail.querySelector('.product__thumbnails-list');

    if (!desktop.matches) {
      stage.style.width = '';
      if (railList) railList.style.maxHeight = '';
      return;
    }

    const slide = stage.querySelector('.product__media:not([hidden])');
    if (!slide) return;
    const ratio = px(getComputedStyle(slide).getPropertyValue('--aspect-ratio')) || px(getComputedStyle(gallery).getPropertyValue('--aspect-ratio')) || 1;

    const headerHeight = measureHeader();
    const room = window.innerHeight - headerHeight - GAP_ABOVE_AND_BELOW;

    // Width available to the stage: the whole column, minus the rail when it sits beside.
    const railBeside = rail && getComputedStyle(container).flexDirection === 'row';
    const gap = px(getComputedStyle(container).columnGap);
    let available = container.clientWidth - (railBeside ? rail.offsetWidth + gap : 0);

    const width = Math.max(0, Math.min(available, room * ratio));
    stage.style.width = Math.floor(width) + 'px';

    if (railList) railList.style.maxHeight = railBeside ? Math.floor(width / ratio) + 'px' : '';
  };

  const fitAll = () => document.querySelectorAll('.product--thumbnail media-gallery').forEach(fitGallery);

  // Debounced with a timer, not requestAnimationFrame: frames are paused in
  // background tabs and the first fit must still land when the tab is shown.
  let timer = 0;
  const scheduleFit = () => {
    clearTimeout(timer);
    timer = setTimeout(fitAll, 60);
  };

  const start = () => {
    fitAll();
    window.addEventListener('resize', scheduleFit, { passive: true });
    desktop.addEventListener('change', scheduleFit);
    document.addEventListener('variant:change', scheduleFit); // a variant can switch to a photo with a different shape
    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver(scheduleFit);
      document.querySelectorAll('.product--thumbnail .product__media-container').forEach((el) => ro.observe(el));
    }
    // Lazy images arriving later can change the slide's reported ratio; re-run once they land.
    window.addEventListener('load', scheduleFit, { once: true });
    document.addEventListener('visibilitychange', scheduleFit);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
